// src/pages/StudentProfilePage.js
import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { students as studentsApi, devices as devicesApi, settings as settingsApi } from '../utils/api';

const STATUS_BADGE = { active: 'badge-success', suspended: 'badge-danger', graduated: 'badge-info', withdrawn: 'badge-muted' };
const DEV_STATUS   = { enabled: 'badge-success', disabled: 'badge-danger', removed: 'badge-muted' };

function AddDeviceModal({ studentId, onClose, onAdded }) {
  const [form, setForm] = useState({ mac_address: '', device_model: '', device_type: 'other', notes: '' });
  const [saving, setSaving] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);

  const handleMacBlur = async () => {
    if (form.mac_address.length < 12) return;
    try {
      const res = await devicesApi.ouiLookup(form.mac_address);
      setLookupResult(res.data.data);
    } catch (_) {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await devicesApi.create({ ...form, student_id: studentId });
      toast.success('Device registered!');
      onAdded(res.data.data);
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.[0]?.msg
        || 'Failed to register device';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">Register New Device</div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">MAC Address *</label>
            <input className="form-control" value={form.mac_address}
              onChange={e => setForm(f => ({ ...f, mac_address: e.target.value }))}
              onBlur={handleMacBlur}
              placeholder="AA:BB:CC:DD:EE:FF" required />
            {lookupResult && (
              <div style={{ fontSize: '0.78rem', color: 'var(--success)', marginTop: 4 }}>
                ✓ Vendor: {lookupResult.vendor || 'Unknown'}
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Device Model</label>
            <input className="form-control" value={form.device_model}
              onChange={e => setForm(f => ({ ...f, device_model: e.target.value }))}
              placeholder="e.g. iPhone 15, Dell Latitude" />
          </div>
          <div className="form-group">
            <label className="form-label">Device Type</label>
            <select className="form-control" value={form.device_type}
              onChange={e => setForm(f => ({ ...f, device_type: e.target.value }))}>
              <option value="laptop">Laptop</option>
              <option value="phone">Phone</option>
              <option value="tablet">Tablet</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Registering…' : '+ Register Device'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function StudentProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showAddDevice, setShowAddDevice] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['student', id],
    queryFn: () => studentsApi.get(id).then(r => r.data.data)
  });

  const { data: appSettings } = useQuery({
    queryKey: ['app-settings'],
    queryFn: () => settingsApi.appSettings().then(r => r.data.data)
  });
  const maxDevices = parseInt(appSettings?.max_devices_per_student ?? 2, 10);

  const statusMut = useMutation({
    mutationFn: ({ devId, status }) => devicesApi.setStatus(devId, status),
    onSuccess: () => { toast.success('Device status updated'); qc.invalidateQueries(['student', id]); },
    onError: err => toast.error(err.response?.data?.message || 'Failed')
  });

  const deleteMut = useMutation({
    mutationFn: devId => devicesApi.delete(devId),
    onSuccess: () => { toast.success('Device deleted'); qc.invalidateQueries(['student', id]); },
    onError: err => toast.error(err.response?.data?.message || 'Failed to delete device')
  });

  if (isLoading) return <div className="loading-page"><span className="spinner" style={{ width: 36, height: 36 }} /></div>;
  if (!data) return <div className="empty-state">Student not found</div>;

  const s = data;
  const activeDevices = s.devices?.filter(d => d.status !== 'removed') || [];
  const enabledCount = activeDevices.filter(d => d.status === 'enabled').length;

  return (
    <div>
      <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginBottom: 8 }}>
        <Link to="/students">Students</Link> → {s.full_name}
      </div>

      {/* Header card */}
      <div className="card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 20, padding: '24px 28px' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: 'var(--primary-dim)',
          border: '2px solid var(--primary)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', fontSize: '1.6rem', flexShrink: 0
        }}>
          {s.gender === 'male' ? '♂' : '♀'}
        </div>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <h2 style={{ fontSize: '1.4rem', fontWeight: 700 }}>{s.full_name}</h2>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
            <span className="text-mono" style={{ color: 'var(--primary)' }}>{s.registration_no}</span>
            {s.course_name && <span>📚 {s.course_name}</span>}
            {s.institution_name && <span>🏛 {s.institution_name}</span>}
            {s.academic_year_label && <span>🎓 Year {s.academic_year_label}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span className={`badge ${STATUS_BADGE[s.status]}`}>{s.status}</span>
          <Link to={`/students/${id}/edit`} className="btn btn-secondary btn-sm">Edit</Link>
        </div>
      </div>

      {/* Details + devices grid */}
      <div className="detail-grid-wide">
        {/* Personal info */}
        <div className="card">
          <div className="card-header"><span className="card-title">Profile Details</span></div>
          {[
            ['Email',        s.email        || '—'],
            ['Phone',        s.phone        || '—'],
            ['Nationality',  s.nationality  || '—'],
            ['Gender',       s.gender       || '—'],
            ['Enrollment Year', s.enrollment_year || '—'],
            ['Registered',   new Date(s.created_at).toLocaleDateString()],
            ['Last Updated', new Date(s.updated_at).toLocaleDateString()]
          ].map(([label, val]) => (
            <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>{label}</span>
              <span style={{ color: 'var(--text)' }}>{val}</span>
            </div>
          ))}
          {s.notes && (
            <div style={{ marginTop: 12, padding: '10px', background: 'var(--bg-hover)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--text-dim)' }}>
              📝 {s.notes}
            </div>
          )}
        </div>

        {/* Devices */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Devices ({enabledCount}/{maxDevices} enabled)</span>
            <button
              className="btn btn-primary btn-sm"
              disabled={enabledCount >= maxDevices}
              title={enabledCount >= maxDevices ? `Limit of ${maxDevices} enabled device(s) reached` : undefined}
              onClick={() => setShowAddDevice(true)}
            >
              + Add Device
            </button>
          </div>
          {enabledCount >= maxDevices && (
            <div style={{ margin: '0 0 12px', padding: '10px 12px', background: 'var(--bg-hover)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--text-dim)' }}>
              ⚠ This student has reached the maximum of {maxDevices} enabled device(s). Disable or delete an existing device before registering a new one.
            </div>
          )}
          {activeDevices.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px' }}>
              <div className="icon">📱</div>
              No devices registered. <br />
              <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => setShowAddDevice(true)}>Register First Device</button>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>MAC Address</th><th>Model</th><th>Type</th><th>Vendor</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {activeDevices.map(d => (
                    <tr key={d.id}>
                      <td><span className="text-mono">{d.mac_address}</span></td>
                      <td>{d.device_model || '—'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{d.device_type}</td>
                      <td style={{ fontSize: '0.78rem' }}>{d.vendor_name || '—'}</td>
                      <td><span className={`badge ${DEV_STATUS[d.status]}`}>{d.status}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {d.status === 'enabled' ? (
                            <button className="btn btn-danger btn-sm"
                              onClick={() => { if (window.confirm(`Disable device ${d.mac_address}? This will cut off its network access.`)) statusMut.mutate({ devId: d.id, status: 'disabled' }); }}>
                              Disable
                            </button>
                          ) : (
                            <>
                              <button className="btn btn-success btn-sm"
                                disabled={enabledCount >= maxDevices}
                                title={enabledCount >= maxDevices ? `Limit of ${maxDevices} enabled device(s) reached — disable another device first` : undefined}
                                onClick={() => { if (window.confirm(`Enable device ${d.mac_address}? This will grant it network access.`)) statusMut.mutate({ devId: d.id, status: 'enabled' }); }}>
                                Enable
                              </button>
                              <button className="btn btn-danger btn-sm"
                                onClick={() => { if (window.confirm('Permanently delete this device? This cannot be undone.')) deleteMut.mutate(d.id); }}>
                                Delete
                              </button>
                            </>
                          )}
                          <Link to={`/devices/${d.id}`} className="btn btn-secondary btn-sm">Details</Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showAddDevice && (
        <AddDeviceModal
          studentId={id}
          onClose={() => setShowAddDevice(false)}
          onAdded={() => qc.invalidateQueries(['student', id])}
        />
      )}
    </div>
  );
}
